import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Target } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, fmtDate } from "../../lib/api";
import { usePatients, useProgress } from "../../hooks/useData";
import { Delta, Kpi, PageHeader, Panel, SimBadge } from "./CareLayout";

export default function Overview() {
  const nav = useNavigate();
  const { data: d } = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get("/dashboard").then((r) => r.data), staleTime: 0 });
  const { data: patients } = usePatients();
  const demo = patients?.find((p) => p.is_demo) || patients?.[0];
  const { data: prog } = useProgress(demo?.id);
  return (
    <div data-testid="care-overview">
      <PageHeader title="Vue d'ensemble" sub="Baobab Care · tableau de bord thérapeute">
        <SimBadge />
        {demo && <button data-testid="open-demo-patient-button" onClick={() => nav(`/care/patients/${demo.id}`)} className="flex h-11 items-center gap-2 rounded-xl bg-brand px-4 text-sm font-bold text-white">Ouvrir {demo.name}<ArrowRight className="h-4 w-4" /></button>}
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Séances réalisées" value={d?.sessions_count} testId="kpi-sessions" loading={!d} />
        <Kpi label="Temps de rééducation" value={d?.total_minutes} unit="min" testId="kpi-minutes" loading={!d} />
        <Kpi label="Patients suivis" value={d?.patients_count} testId="kpi-patients" loading={!d} />
        <Kpi label="Séances planifiées" value={d?.planned_count} testId="kpi-planned" loading={!d} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Panel title={`Précision du mouvement · ${demo?.name || ""}`} className="lg:col-span-2" testId="overview-progress-chart">
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={prog?.series || []}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#17A2A2" stopOpacity={0.35} /><stop offset="1" stopColor="#17A2A2" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#94A3B8" strokeOpacity={0.28} /><XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <Tooltip /><Area type="monotone" dataKey="accuracy_mean" name="Précision (%)" stroke="#17A2A2" strokeWidth={3} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Alertes" testId="overview-alerts">
          {!d?.alerts.length && <p className="text-sm text-muted-foreground">Aucune alerte récente.</p>}
          <ul className="space-y-2">
            {d?.alerts.map((a, i) => (
              <li key={i}><Link to={`/care/sessions/${a.session_id}/report`} className="flex gap-2 rounded-xl bg-amber-50 p-3 text-sm hover:bg-amber-100" data-testid={`alert-item-${i}`}>
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" /><span><b>{a.patient}</b> · {a.message}<span className="block text-xs text-muted-foreground">{fmtDate(a.date)}</span></span></Link></li>
            ))}
          </ul>
        </Panel>
        <Panel title="Objectifs de rééducation en cours" testId="overview-goals">
          {d?.goals.map((g) => (
            <div key={g.patient_id} className="mb-3">
              <p className="text-sm font-bold">{g.patient}</p><p className="text-xs text-muted-foreground">{g.objective}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">{g.goals.map((x) => <span key={x} className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary-strong"><Target className="h-3 w-3" />{x}</span>)}</div>
            </div>
          ))}
        </Panel>
        <Panel title="Progrès récents" className="lg:col-span-2" testId="overview-recent">
          <div className="overflow-x-auto"><table className="w-full min-w-[480px] text-sm">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground/80"><th className="pb-2">Date</th><th>Patient</th><th>Flexion max</th><th>Précision</th><th></th></tr></thead>
            <tbody>
              {d?.recent.map((r) => (
                <tr key={r.session_id} className="border-t border-border/60">
                  <td className="py-2.5">{fmtDate(r.date)}</td><td>{r.patient}</td>
                  <td>{r.rom ?? "—"}° <Delta v={r.delta_rom} unit="°" /></td><td>{r.accuracy ?? "—"}% <Delta v={r.delta_accuracy} /></td>
                  <td className="text-right"><Link className="inline-flex min-h-11 items-center px-2 font-semibold text-primary-strong hover:underline" to={`/care/sessions/${r.session_id}/report`} data-testid={`recent-report-${r.session_id}`}>Rapport</Link></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Panel>
      </div>
    </div>
  );
}
